import moment, { Moment } from 'moment';
import {
	EventType,
	ServiceScheduleItemCaseType,
	ServiceScheduleItemMember,
	ServiceScheduleType,
	useLazyWorkplaceEventsQuery,
	useLazyWorkplaceServiceScheduleQuery,
	WorkplaceMemberRole,
} from '../../../graphql/graphql';
import {
	ServiceScheduleCollapseAbleEvent,
	ServiceScheduleCollapseAbleEventItem,
	ServiceScheduleEvent,
	ServiceScheduleEventCaseType,
	ServiceScheduleEventWorker,
} from '../Interfaces/ServiceScheduleEvent.interface';
import { useState } from 'react';
import useTranslation from '../../../Hooks/useTranslation';
import { getDateTimeWithDst, getMomentDate } from '../../../Utils/dates.util';
import { TimelineCalendarEvent } from '../../Calendar/Components/TimelineCalendar/TimelineCalendar';
import { CalendarView } from '../../Calendar/Enums/CalendarView.eunm';
import { useAppContext } from '../../../Services/AppContext';
import { v4 as uuidv4 } from 'uuid';

export default function useServiceSchedules() {
	const { t } = useTranslation();
	const [getServiceSchedules] = useLazyWorkplaceServiceScheduleQuery();
	const [getWorkplaceEvents] = useLazyWorkplaceEventsQuery();
	const { dateFormatMoment } = useAppContext();

	const [isLoading, setIsLoading] = useState(false);

	const EVENT_PRESET: Record<
		ServiceScheduleType,
		{ label: string; eventColor: string }
	> = {
		[ServiceScheduleType.Shift]: {
			label: 'base.shift',
			eventColor: '#b6d3fc',
		},
		[ServiceScheduleType.ShiftExtra]: {
			label: 'base.extraService',
			eventColor: '#62a2ff',
		},
		[ServiceScheduleType.OnlineOrder]: {
			label: 'component.workplaceOrderingSettingsCalendar.eventTitle',
			eventColor: '#62a2ff',
		},
		[ServiceScheduleType.OnlineConnect]: {
			label: '',
			eventColor: '',
		},
		[ServiceScheduleType.Default]: {
			label: '',
			eventColor: '',
		},
	};

	const prepareDateTime = (
		originDate: string,
		originTime: string,
		date: string,
		time: string,
	): Moment => {
		return getDateTimeWithDst(originDate, date, time, originTime);
	};

	const prepareCaseTypes = (
		serviceScheduleItemCaseTypes: ServiceScheduleItemCaseType[],
	): ServiceScheduleEventCaseType[] => {
		const caseTypes: ServiceScheduleEventCaseType[] = [];

		serviceScheduleItemCaseTypes?.forEach(serviceScheduleItemCaseType => {
			const value = serviceScheduleItemCaseType.workplaceCaseType?.id;
			const label =
				serviceScheduleItemCaseType.workplaceCaseType?.caseType?.label;
			if (value && label) {
				caseTypes.push({
					value,
					label,
					eventInterval:
						serviceScheduleItemCaseType.workplaceCaseType?.caseType
							?.eventInterval ?? undefined,
				});
			}
		});

		return caseTypes;
	};

	const prepareWorkers = (
		serviceScheduleItemMembers: ServiceScheduleItemMember[],
	): ServiceScheduleEventWorker[] => {
		let workers: ServiceScheduleEventWorker[] = [];

		serviceScheduleItemMembers?.forEach(serviceScheduleItemMember => {
			const name =
				serviceScheduleItemMember.workplaceMember?.user?.profile?.fullName;
			const userId = serviceScheduleItemMember.workplaceMember?.user?.id;
			const workplaceMemberId = serviceScheduleItemMember.workplaceMemberId;
			const roles = (serviceScheduleItemMember.workplaceMember
				?.workplaceMemberRoles || []) as WorkplaceMemberRole[];
			if (name && userId && workplaceMemberId) {
				workers.push({
					role: roles,
					name,
					userId,
					workplaceMemberId,
					isDoctor: roles.some(({ role }) => role?.key === 'doctor') ?? false,
				});
			}
		});

		return workers.sort((a, b) => (a.isDoctor ? -1 : 1));
	};

	const getWorkplaceSchedules = async ({
		from,
		to,
		allDay,
		workplaceIds,
		type,
	}: {
		from: Moment;
		to: Moment;
		allDay?: boolean;
		workplaceIds?: string[];
		type: ServiceScheduleType;
	}) => {
		setIsLoading(true);
		const tempEvents: ServiceScheduleEvent[] = [];

		const data = await getServiceSchedules({
			from: from.format(),
			to: to.format(),
			workplaceIds,
			type,
			includeOccurrences: true,
		});

		data?.data?.serviceSchedules?.data?.forEach(serviceSchedule => {
			serviceSchedule?.serviceScheduleItems?.forEach(serviceScheduleItem => {
				if (serviceScheduleItem?.id) {
					const baseEventData = {
						id: serviceScheduleItem.id,
						scheduleId: serviceSchedule.id,
						scheduleItemId: serviceScheduleItem.id,
						serviceScheduleItem,
						backgroundColor: EVENT_PRESET[type].eventColor,
						borderColor: EVENT_PRESET[type].eventColor,
						textColor: '#000',
						editable: false,
						resourceId: serviceSchedule.workplaceId ?? undefined,
						allDay,
						repeatable: serviceScheduleItem.repeatable ?? false,
						workers: prepareWorkers(
							(serviceScheduleItem?.serviceScheduleItemMembers ||
								[]) as ServiceScheduleItemMember[],
						),
						caseTypes: prepareCaseTypes(
							(serviceScheduleItem?.serviceScheduleItemCaseTypes ||
								[]) as ServiceScheduleItemCaseType[],
						),
					};

					const occurrences = serviceScheduleItem?.occurrences;

					if (Array.isArray(occurrences) && occurrences?.length > 0) {
						occurrences.forEach(occurrence => {
							const timeFrom = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeFrom,
							);
							const timeTo = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeTo,
							);
							var duration = moment.duration(
								getMomentDate(timeTo).diff(getMomentDate(timeFrom)),
							);

							tempEvents.push({
								...baseEventData,
								start: moment.utc(timeFrom).format(),
								end: moment
									.utc(timeFrom)
									.add(duration.asSeconds(), 'seconds')
									.format(),
								isOccurrence: true,
								shift: {
									type: serviceSchedule.type ?? undefined,
									timeInterval: `${timeFrom.format('HH:mm')} - ${timeTo.format(
										'HH:mm',
									)}`,
									label: `${t(EVENT_PRESET[type].label)}: ${timeFrom.format(
										'HH:mm',
									)} - ${timeTo.format('HH:mm')}`,
								},
							});
						});
					}
				}
			});
		});

		setIsLoading(false);
		return tempEvents;
	};

	const getAbsenceEventTitleByCalendarView = ({
		calendarView,
		workerName,
	}: {
		calendarView?: CalendarView;
		workerName: string;
	}): string => {
		const text = t('base.shared.absence');
		if (calendarView === CalendarView.LIST_WEEK) {
			return `${text} - ${workerName}`;
		}
		return text;
	};

	const getAbsenceEventsAsTimeline = async ({
		from,
		to,
		workplaceIds,
		calendarView,
	}: {
		from: Moment;
		to: Moment;
		workplaceIds?: string[];
		calendarView?: CalendarView;
	}) => {
		setIsLoading(true);
		const tempEvents: TimelineCalendarEvent[] = [];

		const data = await getWorkplaceEvents({
			start: from.format(),
			end: to.format(),
			types: [EventType.Absence],
			workplaceIds,
		});

		data?.data?.workplaceEvents?.data?.forEach(event => {
			const members: { id: string; name: string }[] = [];
			event?.eventMembers?.forEach((user, index) => {
				const userData = user?.user;
				if (userData?.isDoctor && userData?.id && userData?.profile?.fullName) {
					members.push({ id: userData.id, name: userData.profile.fullName });
				}
			});

			const dateFrom = getMomentDate(event?.dateFrom);
			let dateTo = getMomentDate(event?.dateTo);

			members.forEach(member => {
				tempEvents.push({
					id: event?.id!,
					title: getAbsenceEventTitleByCalendarView({
						calendarView,
						workerName: member.name,
					}),
					start: dateFrom.format(),
					end: dateTo.format(),
					backgroundColor: '#ff7d7d',
					borderColor: '#ff7d7d',
					textColor: '#000',
					resourceId: member.id,
					extendedProps: {
						tooltipText: `${dateFrom.format(
							dateFormatMoment + ', HH:mm',
						)} - ${dateTo.format(dateFormatMoment + ', HH:mm')}`,
					},
				});
			});
		});

		setIsLoading(false);
		return tempEvents;
	};

	const getEventTitleByCalendarView = ({
		calendarView,
		timeFrom,
		timeTo,
		workerName,
		type,
	}: {
		calendarView?: CalendarView;
		timeFrom: Moment;
		timeTo: Moment;
		workerName: string;
		type: ServiceScheduleType;
	}): string => {
		let label = `${timeFrom.format('HH:mm')} - ${timeTo.format('HH:mm')}`;

		if (calendarView !== CalendarView.TIMELINE_DAY) {
			if (type === ServiceScheduleType.Shift) {
				label = t('base.shift');
			} else if (type === ServiceScheduleType.ShiftExtra) {
				label = t('base.extraService');
			}
		}

		if (calendarView === CalendarView.LIST_WEEK) {
			return workerName;
		}
		return label;
	};

	const getSchedulesAsTimeline = async ({
		from,
		to,
		workplaceIds,
		types,
		calendarView,
	}: {
		from: Moment;
		to: Moment;
		workplaceIds?: string[];
		types: ServiceScheduleType[];
		calendarView?: CalendarView;
	}) => {
		setIsLoading(true);
		const tempEvents: TimelineCalendarEvent[] = [];

		const data = await getServiceSchedules({
			from: from.format(),
			to: to.format(),
			workplaceIds,
			types,
			includeOccurrences: true,
		});

		data?.data?.serviceSchedules?.data?.forEach(serviceSchedule => {
			serviceSchedule?.serviceScheduleItems?.forEach(serviceScheduleItem => {
				if (serviceScheduleItem?.id) {
					const serviceScheduleType: ServiceScheduleType =
						serviceSchedule?.type ?? ServiceScheduleType.Default;
					const workers = prepareWorkers(
						(serviceScheduleItem?.serviceScheduleItemMembers ||
							[]) as ServiceScheduleItemMember[],
					);
					workers.forEach(worker => {
						const baseEventData = {
							id: serviceScheduleItem.id,
							backgroundColor: EVENT_PRESET[serviceScheduleType]?.eventColor,
							borderColor: EVENT_PRESET[serviceScheduleType]?.eventColor,
							textColor: '#000',
							resourceId: worker.userId,
						};

						const occurrences = serviceScheduleItem?.occurrences;

						if (Array.isArray(occurrences) && occurrences?.length > 0) {
							occurrences.forEach(occurrence => {
								const timeFrom = prepareDateTime(
									serviceSchedule.dateFrom,
									serviceScheduleItem.timeFrom,
									occurrence,
									serviceScheduleItem.timeFrom,
								);
								const timeTo = prepareDateTime(
									serviceSchedule.dateFrom,
									serviceScheduleItem.timeFrom,
									occurrence,
									serviceScheduleItem.timeTo,
								);
								var duration = moment.duration(
									getMomentDate(timeTo).diff(getMomentDate(timeFrom)),
								);

								tempEvents.push({
									...baseEventData,
									start: moment.utc(timeFrom).format(),
									end: moment
										.utc(timeFrom)
										.add(duration.asSeconds(), 'seconds')
										.format(),
									title: getEventTitleByCalendarView({
										calendarView,
										timeFrom,
										timeTo,
										workerName: worker.name,
										type: serviceSchedule.type as ServiceScheduleType,
									}),
									extendedProps: {
										serviceSchedule,
										serviceScheduleItem,
										timeFrom,
										timeTo,
										tooltipText: `${timeFrom.format('HH:mm')} - ${timeTo.format(
											'HH:mm',
										)}`,
										resourceId: worker.userId,
									},
								});
							});
						}
					});
				}
			});
		});

		setIsLoading(false);
		return tempEvents;
	};

	const groupByDate = (
		schedule: ServiceScheduleCollapseAbleEventItem[],
	): { date: string; items: ServiceScheduleCollapseAbleEventItem[] }[] => {
		const groups: Record<string, ServiceScheduleCollapseAbleEventItem[]> =
			schedule.reduce(
				(acc: Record<string, ServiceScheduleCollapseAbleEventItem[]>, item) => {
					const date = item.from.format('YYYY-MM-DD');
					if (!acc[date]) {
						acc[date] = [];
					}
					acc[date].push(item);
					return acc;
				},
				{},
			);

		return Object.entries(groups).map(([date, items]) => ({
			date,
			items,
		}));
	};

	const findFirstSchedule = (
		schedules: ServiceScheduleCollapseAbleEventItem[],
	) => {
		if (!schedules || schedules.length === 0) {
			return {
				firstSchedule: undefined,
				otherCount: 0,
				otherSchedules: [],
			};
		}

		const firstItem = schedules.reduce((earliest, current) => {
			const currentFrom = moment(current.from);
			const earliestFrom = moment(earliest.from);

			if (currentFrom.isBefore(earliestFrom)) {
				return current;
			} else if (currentFrom.isSame(earliestFrom)) {
				const currentIsDoctor = current.workers?.some(w => w.isDoctor);
				const earliestIsDoctor = earliest.workers?.some(w => w.isDoctor);
				if (currentIsDoctor && !earliestIsDoctor) {
					return current;
				}
			}
			return earliest;
		}, schedules[0]);

		const firstWorker = firstItem.workers?.[0];
		const timeLabel = firstItem.shift?.label || '';
		const firstScheduleLabel = firstWorker
			? `${firstWorker.name} ${timeLabel}`
			: timeLabel;

		const otherSchedules = schedules.filter(
			item => item.scheduleItemId !== firstItem.scheduleItemId,
		);

		return {
			firstScheduleLabel,
			otherCount: otherSchedules.length,
			otherSchedules,
		};
	};

	const getSchedulesAsCollapseAbleEvents = async ({
		from,
		to,
		workplaceIds,
		type,
	}: {
		from: Moment;
		to: Moment;
		workplaceIds?: string[];
		type: ServiceScheduleType;
	}): Promise<ServiceScheduleCollapseAbleEvent[]> => {
		setIsLoading(true);
		const tempEvents: ServiceScheduleCollapseAbleEvent[] = [];
		const items: ServiceScheduleCollapseAbleEventItem[] = [];

		const data = await getServiceSchedules({
			from: from.format(),
			to: to.format(),
			workplaceIds,
			type,
			includeOccurrences: true,
		});

		data?.data?.serviceSchedules?.data?.forEach(serviceSchedule => {
			if (serviceSchedule) {
				serviceSchedule?.serviceScheduleItems?.forEach(serviceScheduleItem => {
					const occurrences = serviceScheduleItem?.occurrences;

					if (Array.isArray(occurrences) && occurrences?.length > 0) {
						serviceScheduleItem?.occurrences?.forEach(occurrence => {
							const timeFrom = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeFrom,
							);
							const timeTo = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeTo,
							);

							items.push({
								scheduleItemId: serviceScheduleItem.id,
								from: timeFrom,
								to: timeTo,
								shift: {
									timeInterval: `${timeFrom.format('HH:mm')} - ${timeTo.format(
										'HH:mm',
									)}`,
									label: `${timeFrom.format('HH:mm')} - ${timeTo.format(
										'HH:mm',
									)}`,
								},
								workers: prepareWorkers(
									(serviceScheduleItem?.serviceScheduleItemMembers ||
										[]) as ServiceScheduleItemMember[],
								),
							});
						});
					}
				});
			}
		});

		const groupedByDate = groupByDate(items);

		groupedByDate.forEach(item => {
			const { firstScheduleLabel, otherSchedules, otherCount } =
				findFirstSchedule(item.items);
			tempEvents.push({
				id: uuidv4(),
				type,
				start: moment.utc(item.date).format(),
				end: moment.utc(item.date).format(),
				label: firstScheduleLabel,
				backgroundColor: EVENT_PRESET[type].eventColor,
				borderColor: EVENT_PRESET[type].eventColor,
				textColor: '#000',
				allDay: true,
				extendedProps: {
					items: otherSchedules,
					otherCount,
				},
			});
		});
		setIsLoading(false);
		return tempEvents;
	};

	return {
		getWorkplaceSchedules,
		getSchedulesAsTimeline,
		getAbsenceEventsAsTimeline,
		getSchedulesAsCollapseAbleEvents,
		isLoading,
	};
}
